/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface FuelTankProps {
  id: string;
  code: string;
  name: string;
  operationsStarted: Date | null;
  operationsEnded: Date | null;
  locationId: string;
  sourceSystemId: number | null;
  baseId: string | null;
  locationName: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class FuelTankRecord extends Record<FuelTankProps>({
  id: null as any,
  code: null as any,
  name: null as any,
  operationsStarted: null as any,
  operationsEnded: null as any,
  locationId: null as any,
  sourceSystemId: null as any,
  baseId: null as any,
  locationName: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    code: string;
    name: string;
    operationsStarted: string | number | Date | null;
    operationsEnded: string | number | Date | null;
    locationId: string;
    sourceSystemId: number | null;
    baseId: string | null;
    locationName: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): FuelTankRecord {
    if (json == null) {
      throw new Error('FuelTankRecord.fromJS: parameter cannot be null');
    }
    return new FuelTankRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'FuelTankJson.id',
      ),
      code: getFromOrThrow(json, (j) => j.code, 'FuelTankJson.code'),
      name: getFromOrThrow(json, (j) => j.name, 'FuelTankJson.name'),
      operationsStarted: json.operationsStarted != null ? new Date(json.operationsStarted) : null,
      operationsEnded: json.operationsEnded != null ? new Date(json.operationsEnded) : null,
      locationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
        'FuelTankJson.locationId',
      ),
      sourceSystemId: json.sourceSystemId,
      baseId: json.baseId,
      locationName: json.locationName,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'FuelTankJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    code: string;
    name: string;
    operationsStarted: string | number | Date | null;
    operationsEnded: string | number | Date | null;
    locationId: string;
    sourceSystemId: number | null;
    baseId: string | null;
    locationName: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [FuelTankRecord, Error[]] {
    if (json == null) {
      throw new Error('FuelTankRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new FuelTankRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'FuelTankJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'FuelTankJson.code'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'FuelTankJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationsStarted: tryOrDefault(
        () => (json.operationsStarted != null ? new Date(json.operationsStarted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationsEnded: tryOrDefault(
        () => (json.operationsEnded != null ? new Date(json.operationsEnded) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
            'FuelTankJson.locationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceSystemId: tryOrDefault(
        () => json.sourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () => json.baseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationName: tryOrDefault(
        () => json.locationName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'FuelTankJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: FuelTankProps) {
    super(props);
    if (props == null) {
      throw new Error('FuelTankRecord constructor parameter cannot be null');
    }
  }
}

export abstract class FuelTankViewModelBase implements FuelTankProps {
  protected readonly _props: FuelTankProps;
  private _json?: FuelTankProps;

  get id(): string {
    return this._props.id;
  }
  get code(): string {
    return this._props.code;
  }
  get name(): string {
    return this._props.name;
  }
  get operationsStarted(): Date | null {
    return this._props.operationsStarted;
  }
  get operationsEnded(): Date | null {
    return this._props.operationsEnded;
  }
  get locationId(): string {
    return this._props.locationId;
  }
  get sourceSystemId(): number | null {
    return this._props.sourceSystemId;
  }
  get baseId(): string | null {
    return this._props.baseId;
  }
  get locationName(): string | null {
    return this._props.locationName;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: FuelTankProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        code: this._props.code,
        name: this._props.name,
        operationsStarted: this._props.operationsStarted,
        operationsEnded: this._props.operationsEnded,
        locationId: this._props.locationId,
        sourceSystemId: this._props.sourceSystemId,
        baseId: this._props.baseId,
        locationName: this._props.locationName,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllFuelTankEntities = (state: AppState) => state.entityStores.fuelTank.entities;

export const selectFuelTankById = (id: string) => (state: AppState) =>
  state.entityStores.fuelTank.entities.get(id);

export const selectFuelTankMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.fuelTank.meta.get(id);

export const selectFuelTankAndMetadataById = (id: string) =>
  createSelector(selectFuelTankById(id), selectFuelTankMetadataById(id), (fuelTank, meta) => ({
    fuelTank,
    meta,
  }));

export function selectFuelTankEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllFuelTankEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, FuelTankRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectFuelTankEntitiesByCode(code: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}

export function selectFuelTankEntitiesByName(name: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectFuelTankEntitiesByOperationsStarted(operationsStarted: Date) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.operationsStarted === operationsStarted).valueSeq(),
  );
}

export function selectFuelTankEntitiesByOperationsEnded(operationsEnded: Date) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.operationsEnded === operationsEnded).valueSeq(),
  );
}

export function selectFuelTankEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectFuelTankEntitiesBySourceSystemId(sourceSystemId: number) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.sourceSystemId === sourceSystemId).valueSeq(),
  );
}

export function selectFuelTankEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectFuelTankEntitiesByLocationName(locationName: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.locationName === locationName).valueSeq(),
  );
}

export function selectFuelTankEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllFuelTankEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
